import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import {
  generateForwardCurveRequest,
  generateFundamentalsRequest,
  generateLivePricesRequest,
  generatePricesRequest,
} from '../../../functions/buildRequest';

import { DateInput, dateInputToDateTime, validateDate } from '../../types/date-input';
import { QueryParams } from '../../types/query-params';
import { Refreshable } from '../../types/refreshable';
import { ExcelService } from '../../services/excel.service';
import { FeederFilterComponent } from '../feeder-filter/feeder-filter.component';
import { getLocalizedBoolean } from '../../../functions/util';
import { MenuService } from '../../services/menu.service';
import { FEEDER_TYPES, FeederMenuType } from '../../types/menu';
import { BehaviorSubject } from 'rxjs';

/* global console, Office, Excel, require, setTimeout, window, $, localStorage */

@Component({
  selector: 'app-feeder',
  template: require('./feeder.component.html'),
})
export class FeederComponent implements OnInit, Refreshable {
  readonly FEEDER_TYPES = FEEDER_TYPES;

  selected_items: any[] = [];
  products: any[] = [];
  selected_products: any[] = [];
  selection: any[] = [];

  selected: boolean = false;
  inWebsite: boolean = false;
  front_product: boolean = false;

  spot: boolean = false;
  diagram: boolean = false;
  detailed: boolean = false;
  query_all: boolean = false;

  error: boolean = false;
  errorMessage: string = '';

  excel_function: string = '';
  type: FeederMenuType;
  headers = ['bid', 'ask', 'last', 'close', 'change', 'change_percent', 'time'];
  queryParams = new QueryParams();
  search = false;

  quStart: DateInput = { year: -1, month: -1, day: -1 };
  quEnd: DateInput = { year: -1, month: -1, day: -1 };

  errorClass = 'alert alert-danger mb-5';
  product: string = '';
  loadedPrices: any[] = [];

  @ViewChild(FeederFilterComponent)
  private feederFilterComponent!: FeederFilterComponent;

  menu$: BehaviorSubject<any>;
  menuReady$: BehaviorSubject<boolean>;

  constructor(
    @Inject(ExcelService) private excelService: ExcelService,
    @Inject(MenuService) private menuService: MenuService
  ) {
  }

  ngOnInit() {
    this.error = false;
    let date = new Date();
    this.quStart = {
      year: date.getFullYear() - 1,
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
    this.quEnd = {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
    if (
      Office.context.platform !== Office.PlatformType.PC &&
      Office.context.platform !== Office.PlatformType.OfficeOnline
    ) {
      this.inWebsite = true;
    }
    this.menu$ = this.menuService.menu$;
    this.menuReady$ = this.menuService.menuReady$;
    this.prepareMenu();
  }

  private async prepareMenu() {
    this.menuService.loadMenu();
  }

  runHistoric(writeToExcel: boolean = true) {
    if (!this.inWebsite) {
      if (
        (this.selected_products.length > 0 && this.search) ||
        (this.selected_products.length > 0 &&
          (this.type === FEEDER_TYPES.FUTURES ||
            this.type === FEEDER_TYPES.SPOT ||
            this.type === FEEDER_TYPES.ICIS_LONG_TERM_MODEL))
      ) {
        if (this.products.length > 1 && !validateDate(this.quStart) && !this.queryParams.latest_price) {
          this.defaultErrorHandler({
            message: 'To request more than one product you have to provide the start of the query period',
          });
        } else {
          let historic_products: any[] = [];
          this.selected_products.forEach((p) => {
            if (typeof p === typeof '') {
              historic_products.push(p);
            } else {
              historic_products.push(p.product_code);
            }
          });
          if (this.queryParams.nv_default === 'skip' && historic_products.length > 1) {
            this.defaultErrorHandler({
              message: 'When using the skip option as nv_default only one product can be requested',
            });
          } else {
            try {
              if (writeToExcel) {
                this.excelService.runHistoric(
                  historic_products,
                  this.queryParams,
                  this.diagram ? 'diagram' : 'default',
                  this.query_all
                    ? undefined
                    : validateDate(this.quStart)
                      ? dateInputToDateTime(this.quStart)
                      : undefined,
                  this.query_all ? undefined : validateDate(this.quEnd) ? dateInputToDateTime(this.quEnd) : undefined
                );
              } else {
                this.queryParams.max_version = '';
                return generatePricesRequest(
                  historic_products,
                  this.queryParams,
                  this.query_all
                    ? undefined
                    : validateDate(this.quStart)
                      ? dateInputToDateTime(this.quStart)
                      : undefined,
                  this.query_all ? undefined : validateDate(this.quEnd) ? dateInputToDateTime(this.quEnd) : undefined
                );
              }
              this.error = false;
            } catch (error) {
              this.defaultErrorHandler(error);
            }
          }
        }
      } else {
        this.defaultErrorHandler({ message: 'No products selected' });
      }
    }
    return '';
  }

  runForwardCurve(writeToExcel: boolean = true) {
    if (!this.inWebsite) {
      try {
        if (this.queryParams.nv_default === 'skip' && this.selected_products.length > 1) {
          this.defaultErrorHandler({
            message: 'When using the skip option as nv_default only one product can be requested',
          });
        } else {
          let forward_products: any[] = [];
          this.selected_products.forEach((p) => {
            if (typeof p === typeof '') {
              forward_products.push(p);
            } else {
              forward_products.push(p.product_code);
            }
          });
          if (writeToExcel) {
            this.excelService.runForwardCurve(
              forward_products,
              this.queryParams,
              this.diagram ? 'diagram' : 'default',
              dateInputToDateTime(this.quStart),
              dateInputToDateTime(this.quEnd)
            );
          } else {
            return generateForwardCurveRequest(
              forward_products,
              this.queryParams,
              dateInputToDateTime(this.quStart),
              dateInputToDateTime(this.quEnd)
            );
          }
        }
      } catch (error) {
        this.defaultErrorHandler(error);
      }
    }
    return '';
  }

  runLive(writeToExcel: boolean = true) {
    const menu = this.menuService.menu$.value;
    if (!this.inWebsite) {
      if (this.type === FEEDER_TYPES.LIVE_PRICES) {
        if (this.search) {
          const ids: any[] = [];
          this.selected_products.forEach((product) => {
            ids.push(product.id);
          });
          if (writeToExcel) {
            this.excelService.runLiveByIds(ids, this.headers, this.queryParams.unit!, this.queryParams.currency!);
          } else {
            return generateLivePricesRequest(ids, this.queryParams.unit!, this.queryParams.currency!);
          }
          this.error = false;
        } else {
          let market_area_id;
          let product = this.selected_items.length === 6 ? this.selected_items[5] : '';
          if (this.selected_items.length === 6) {
            market_area_id =
              menu[this.selected_items[0]][this.selected_items[1]][this.selected_items[2]][this.selected_items[3]][
                this.selected_items[4]
              ][this.selected_items[5]][0].market_area_id;
          } else {
            this.selection =
              menu[this.selected_items[0]][this.selected_items[1]][this.selected_items[2]][this.selected_items[3]][
                this.selected_items[4]
              ];
            market_area_id = this.selection[Object.keys(this.selection)[0] as any][0].market_area_id;
          }
          try {
            if (writeToExcel) {
              this.excelService.runLive(
                market_area_id,
                this.selected_items[3],
                this.selected_items[4],
                product,
                this.headers,
                this.queryParams.unit!,
                this.queryParams.currency!
              );
            } else {
              let items: any[] = [];
              if (this.selected_items.length === 6) {
                items =
                  menu[this.selected_items[0]][this.selected_items[1]][this.selected_items[2]][this.selected_items[3]][
                    this.selected_items[4]
                  ][this.selected_items[5]];
              } else {
                const productsObj =
                  menu[this.selected_items[0]][this.selected_items[1]][this.selected_items[2]][this.selected_items[3]][
                    this.selected_items[4]
                  ];
                if (productsObj !== undefined && productsObj !== null) {
                  const products = Object.keys(productsObj);
                  products.forEach((productKey) => {
                    items = items.concat(productsObj[productKey]);
                  });
                }
              }
              const ids: any[] = [];
              items.forEach((item) => {
                ids.push(item.id);
              });
              return generateLivePricesRequest(ids, this.queryParams.unit!, this.queryParams.currency!);
            }
            this.error = false;
          } catch (error) {
            this.defaultErrorHandler(error);
          }
        }
      }
    }
    return '';
  }

  runFundamental(writeToExcel: boolean = true) {
    if (!this.inWebsite) {
      if (
        this.selected_products.length > 0 &&
        (this.type === FEEDER_TYPES.FUNDAMENTAL_DATA || this.type === FEEDER_TYPES.ICIS_LONG_TERM_MODEL)
      ) {
        if (this.products.length > 1 && !validateDate(this.quStart)) {
          this.defaultErrorHandler({
            message: 'To request more than one product you have to provide the start of the query period',
          });
        } else {
          let fundamentals: any[] = [];
          if (this.products.length === 0) {
            this.selected_products.forEach((selProduct) => {
              fundamentals.push(selProduct.product_code);
            });
          } else {
            this.products.forEach((product) => {
              this.selected_products.forEach((selProduct) => {
                if (selProduct.toUpperCase() === product.product_code.toUpperCase()) {
                  fundamentals.push(product.product_code);
                }
              });
            });
          }
          try {
            if (writeToExcel) {
              this.excelService.runFundamental(
                fundamentals,
                validateDate(this.quStart) ? dateInputToDateTime(this.quStart) : undefined,
                validateDate(this.quEnd) ? dateInputToDateTime(this.quEnd) : undefined,
                false,
                this.queryParams,
                this.diagram ? 'diagram' : ''
              );
            } else {
              return generateFundamentalsRequest(
                fundamentals,
                this.queryParams,
                validateDate(this.quStart) ? dateInputToDateTime(this.quStart) : undefined,
                validateDate(this.quEnd) ? dateInputToDateTime(this.quEnd) : undefined
              );
            }
            this.error = false;
          } catch (error) {
            this.defaultErrorHandler(error);
          }
        }
      } else {
        this.defaultErrorHandler({ message: 'No products selected' });
      }
    }
    return '';
  }

  run(writeToExcel: boolean = true) {
    if (
      this.type === FEEDER_TYPES.ICIS_LONG_TERM_MODEL &&
      this.selected_products.length > 0 &&
      this.checkICISType(this.selected_products) === undefined
    ) {
      this.defaultErrorHandler({
        message: 'Can not request types of price and fundamental data at the same time',
      });
      this.errorClass = 'alert alert-warning mb-5';
    } else {
      if (
        this.type === FEEDER_TYPES.FUTURES ||
        this.type === FEEDER_TYPES.SPOT ||
        (this.type === FEEDER_TYPES.ICIS_LONG_TERM_MODEL &&
          ((this.selected_products.length > 0 && this.checkICISType(this.selected_products) === 'price') ||
            (this.selected_items.length > 0 && this.selected_items[1].toUpperCase() === 'Prices'.toUpperCase())))
      ) {
        return this.runHistoric(writeToExcel);
      } else if (this.type === FEEDER_TYPES.LIVE_PRICES) {
        return this.runLive(writeToExcel);
      } else if (
        this.type === FEEDER_TYPES.FUNDAMENTAL_DATA ||
        (this.type === FEEDER_TYPES.ICIS_LONG_TERM_MODEL &&
          ((this.selected_products.length > 0 && this.checkICISType(this.selected_products) === 'fundamental') ||
            (this.selected_items.length > 0 && this.selected_items[1].toUpperCase() !== 'Prices'.toUpperCase())))
      ) {
        return this.runFundamental(writeToExcel);
      } else if (this.type === FEEDER_TYPES.FORWARD_CURVE) {
        return this.runForwardCurve(writeToExcel);
      }
    }
    return '';
  }

  checkICISType(products: any[]) {
    const tmpProducts: any[] = [];
    products.forEach((product) =>
      tmpProducts.push(product.product_code !== undefined ? product.product_code : product)
    );
    let type: string | undefined = '';
    let typeSet = false;
    tmpProducts.every((product) => {
      let tmpType = product.split(' ')[0].toUpperCase() === 'Power'.toUpperCase() ? 'price' : 'fundamental';
      if (!typeSet) {
        type = tmpType;
        typeSet = true;
        return true;
      } else {
        if (type === tmpType) {
          return true;
        } else {
          type = undefined;
          return false;
        }
      }
    });
    return type;
  }

  generate() {
    if (this.products.length === 0 && this.type !== FEEDER_TYPES.LIVE_PRICES) {
      this.defaultErrorHandler({ message: 'No product selected' });
    } else if (this.products.length > 1 && !validateDate(this.quStart)) {
      this.defaultErrorHandler({
        message: 'To request more than one product you have to provide the start of the query period',
      });
    } else {
      let historic_products: any[] = [];
      if (this.type === FEEDER_TYPES.FUTURES || this.type === FEEDER_TYPES.FUNDAMENTAL_DATA) {
        this.selected_products.forEach((p) => {
          historic_products.push(p);
        });
      } else {
        this.products.forEach((p) => {
          historic_products.push(p.product_code);
        });
      }
      if (this.type === FEEDER_TYPES.FUTURES) {
        this.excel_function = `=INERCOMP.GETHISTORICPRICES("${historic_products.join(
          ','
        )}";"${this.queryParams.currency}";"${this.queryParams.unit}";"${
          this.queryParams.resolution
        }";"${this.queryParams.aggregation}";"${
          this.queryParams.aggregation_scope
        }";"${this.queryParams.aggregation_method}";"${
          this.query_all
            ? ''
            : validateDate(this.quStart)
              ? dateInputToDateTime(this.quStart).toFormat('yyyy-MM-dd')
              : ''
        }";"${
          this.query_all ? '' : validateDate(this.quEnd) ? dateInputToDateTime(this.quEnd).toFormat('yyyy-MM-dd') : ''
        }"; ${getLocalizedBoolean(
          this.queryParams.latest_price
        )};"default"; ${getLocalizedBoolean(this.queryParams.auto_update)};"${
          this.queryParams.nv_default
        }";${this.queryParams.timezone})`;
      } else if (this.type === FEEDER_TYPES.LIVE_PRICES) {
        this.excel_function = `=INERCOMP.GETLIVEPRICES("${
          this.selected_items[2]
        }";"${this.selected_items[3]}";"${this.selected_items[4]}";"${
          this.selected_items[5] === undefined ? '' : this.selected_items[5]
        }";"${this.headers.join(',')}";60)`;
      } else if (this.type === FEEDER_TYPES.FUNDAMENTAL_DATA) {
        this.excel_function = `INERCOMP.GETFUNDAMENTALDETAILED("${historic_products.join(',')}", "${
          validateDate(this.quStart) ? dateInputToDateTime(this.quStart).toFormat('yyyy-MM-dd') : ''
        }", "${
          validateDate(this.quEnd) ? dateInputToDateTime(this.quEnd).toFormat('yyyy-MM-dd') : ''
        }", ${getLocalizedBoolean(this.detailed)})`;
      } else if (this.type === FEEDER_TYPES.FORWARD_CURVE) {
        this.excel_function = `INERCOMP.GETFORWARDCURVE()`;
      }
      this.error = false;
    }
  }

  selectedChanged(selected: boolean) {
    if (!selected) {
      this.error = false;
      this.front_product = false;
      this.selected = false;
      this.loadedPrices.length = 0;
      this.products.length = 0;
      this.selected_products.length = 0;
    } else {
      this.selected = selected;
      let tmpLoadedPrices: any = JSON.parse(JSON.stringify(this.menuService.menu$.value));
      this.selected_items.forEach((item) => {
        tmpLoadedPrices = tmpLoadedPrices[item];
      });
      this.loadedPrices = tmpLoadedPrices;
      if (
        this.type === FEEDER_TYPES.FUNDAMENTAL_DATA ||
        this.type === FEEDER_TYPES.SPOT ||
        this.type === FEEDER_TYPES.ICIS_LONG_TERM_MODEL
      ) {
        this.products = this.loadedPrices;
        this.filterProducts();
      }
      if (this.type === FEEDER_TYPES.FUTURES || this.type === FEEDER_TYPES.FUNDAMENTAL_DATA) {
        // this.filterProducts();
        this.product = this.selected_items[this.selected_items.length - 1];
      }
      if (this.type === FEEDER_TYPES.FORWARD_CURVE) {
        this.products = this.loadedPrices.filter((price) => {
          return (
            !price.product_code.toUpperCase().includes('Base-MonthlyMeans'.toUpperCase()) &&
            !price.product_code.toUpperCase().includes('Peak-MonthlyMeans'.toUpperCase())
          );
        });
        this.filterProducts();
      }
    }
  }

  setQueryStart(periodString: string) {
    let date = new Date();
    switch (periodString) {
      case '1M':
        date.setMonth(date.getMonth() - 1);
        break;
      case '3M':
        date.setMonth(date.getMonth() - 3);
        break;
      case '12M':
        date.setFullYear(date.getFullYear() - 1);
    }
    this.quStart.year = date.getFullYear();
    this.quStart.month = date.getMonth() + 1;
    this.quStart.day = date.getDate();
  }

  removeAll() {
    this.selected_products.length = 0;
  }

  reset() {
    this.error = false;
    this.front_product = false;
    this.selected = false;
    this.loadedPrices.length = 0;
    this.products.length = 0;
    this.selected_products.length = 0;
    this.queryParams = new QueryParams();
    this.query_all = false;
    this.diagram = false;
  }

  defaultErrorHandler(error: any, errorClass?: any) {
    this.error = true;
    this.errorMessage = error.message;
    console.error(error);
    this.errorClass = errorClass !== undefined ? errorClass : 'alert alert-danger mb-5';
  }

  refresh() {
    this.selected_items.length = 0;
    this.type = undefined;
    this.products.length = 0;
    this.selected = false;
    this.selected_products.length = 0;
    this.ngOnInit();
  }

  changeSelection(price: any) {
    if (this.selected_products.includes(price.product_code)) {
      this.selected_products.splice(this.selected_products.indexOf(price.product_code), 1);
    } else {
      this.selected_products.push(price.product_code);
    }
  }

  inSelectedProducts(product_code: any) {
    return this.selected_products.includes(product_code);
  }

  filterProducts() {
    if (this.selected_items[1].toUpperCase() !== 'EXCHANGE RATE') {
      this.products.forEach((product) => {
        if (!this.inSelectedProducts(product.product_code)) {
          this.selected_products.push(product.product_code);
        }
      });
    }
    if (this.products.length === 0 && !this.spot && !this.front_product) {
      this.defaultErrorHandler({
        message: 'No products in selected timespan of "From delivery start" and "To delivery start"',
      });
      this.errorClass = 'alert alert-warning mb-5';
    } else {
      this.error = false;
    }
    this.products.sort((a, b) => {
      if (
        a.delivery_start !== undefined &&
        a.delivery_start !== null &&
        b.delivery_start !== undefined &&
        b.delivery_start !== null
      ) {
        let dateA = a.delivery_start.split('T')[0];
        let dateB = b.delivery_start.split('T')[0];
        let deliveryStartA = new Date(
          Number(dateA.split('-')[0]),
          Number(dateA.split('-')[1]) - 1,
          Number(dateA.split('-')[2])
        );
        let deliveryStartB = new Date(
          Number(dateB.split('-')[0]),
          Number(dateB.split('-')[1]) - 1,
          Number(dateB.split('-')[2])
        );
        return (
          deliveryStartA.getTime() - deliveryStartB.getTime() ||
          (a.product_code > b.product_code ? 1 : a.product_code < b.product_code ? -1 : 0)
        );
      }
      return 0;
    });
  }

  setType(type: FeederMenuType) {
    if (
      ((type === FEEDER_TYPES.FORWARD_CURVE || type === FEEDER_TYPES.ICIS_LONG_TERM_MODEL) &&
        this.type !== FEEDER_TYPES.FORWARD_CURVE &&
        this.type !== FEEDER_TYPES.ICIS_LONG_TERM_MODEL) ||
      ((this.type === FEEDER_TYPES.FORWARD_CURVE || this.type === FEEDER_TYPES.ICIS_LONG_TERM_MODEL) &&
        type !== FEEDER_TYPES.FORWARD_CURVE &&
        type !== FEEDER_TYPES.ICIS_LONG_TERM_MODEL) ||
      !type
    ) {
      this.type = type;
      this.resetQueryPeriod();
    } else {
      this.type = type;
    }
    if (!this.search) {
      this.reset();
    }
  }

  createPQUrl() {
    const clipboardUri: string = this.run(false) || '';
    navigator.clipboard.writeText(clipboardUri).then(() => {});
  }

  resetQueryPeriod() {
    let preferred_query_period = localStorage.getItem('preferred_query_period');
    if (this.type === FEEDER_TYPES.FORWARD_CURVE || this.type === FEEDER_TYPES.ICIS_LONG_TERM_MODEL) {
      this.quStart = { year: new Date().getFullYear(), month: 1, day: 1 };
      this.quEnd = { year: new Date().getFullYear() + 4, month: 12, day: 31 };
    } else {
      if (preferred_query_period !== undefined && preferred_query_period !== null) {
        this.setQueryStart(preferred_query_period);
        const date = new Date();
        this.quEnd = {
          year: date.getFullYear(),
          month: date.getMonth() + 1,
          day: date.getDate(),
        };
      } else {
        let date = new Date();
        date.setFullYear(date.getFullYear() - 1);
        this.quStart = {
          year: date.getFullYear(),
          month: date.getMonth() + 1,
          day: date.getDate(),
        };
        this.quEnd = {
          year: new Date().getFullYear(),
          month: new Date().getMonth() + 1,
          day: new Date().getDate(),
        };
      }
    }
  }

  restoreDefaults() {
    this.diagram = false;
    this.queryParams = new QueryParams();
    this.resetQueryPeriod();
    if (this.feederFilterComponent !== undefined) {
      this.feederFilterComponent.restoreDefaults();
    }
  }

  getChipColor(productCode: string) {
    return this.inSelectedProducts(productCode) ? 'primary' : 'accent';
  }

  autoUpdateChanged() {
    this.queryParams.nv_default = '';
    this.queryParams = JSON.parse(JSON.stringify(this.queryParams));
  }
}
